import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ethers } from 'ethers';
import {
  formatOnChainTokenAmount,
  type OnChainTokenBalance,
  type OnChainTokenSymbol,
} from '../lib/blockchain';
import { sendTokenWithMetaMask, sendTokenWithWorldApp, waitForOnChainTransaction } from '../lib/sendToken';
import { buildWalletOperationIdempotencyKey, recordWalletOperationBestEffort } from '../lib/walletActivity';
import WalletSourceIcon from './WalletSourceIcon';
import type { WalletBalanceSource } from '../lib/walletSource';
import { formatErrorWithCode, formatMessageWithCode } from '../i18n';

export type SendTokenOption = {
  symbol: OnChainTokenSymbol;
  label: string;
  balance?: OnChainTokenBalance;
};

type SendTokenModalProps = {
  isOpen: boolean;
  initialSymbol: OnChainTokenSymbol | null;
  options: SendTokenOption[];
  isWorldApp: boolean;
  authUserId: string | null;
  balanceSource: WalletBalanceSource;
  walletAddress: string | null;
  isMetaMaskLoading: boolean;
  metaMaskError: string | null;
  onConnectMetaMask: () => Promise<void>;
  walletAppDeepLink: string | null;
  onClose: () => void;
  onSubmitted: () => void;
};

export default function SendTokenModal({
  isOpen,
  initialSymbol,
  options,
  isWorldApp,
  authUserId,
  balanceSource,
  walletAddress,
  isMetaMaskLoading,
  metaMaskError,
  onConnectMetaMask,
  walletAppDeepLink,
  onClose,
  onSubmitted,
}: SendTokenModalProps) {
  const { t } = useTranslation();
  const authUserIdRef = useRef(authUserId);
  const walletAddressRef = useRef(walletAddress);
  authUserIdRef.current = authUserId;
  walletAddressRef.current = walletAddress;
  const availableOptions = useMemo(
    () => options.filter((option) => option.balance && !option.balance.error),
    [options],
  );
  const [selectedSymbol, setSelectedSymbol] = useState<OnChainTokenSymbol | null>(initialSymbol);
  const [recipientAddress, setRecipientAddress] = useState('');
  const [amount, setAmount] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [txHash, setTxHash] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const selectedOption = availableOptions.find((option) => option.symbol === selectedSymbol)
    ?? availableOptions[0];
  const selectedBalance = selectedOption?.balance;
  const isWorldAppSource = isWorldApp && balanceSource === 'world-app';
  const sourceLabel = balanceSource === 'world-app' ? 'World App' : 'MetaMask';
  const canSend = isWorldAppSource || balanceSource === 'metamask';

  useEffect(() => {
    if (!isOpen) return;
    setSelectedSymbol(initialSymbol && availableOptions.some((option) => option.symbol === initialSymbol)
      ? initialSymbol
      : availableOptions[0]?.symbol ?? null);
    setRecipientAddress('');
    setAmount('');
    setTxHash('');
    setStatusMessage('');
    setErrorMessage('');
    setIsSending(false);
    setIsConfirming(false);
  }, [availableOptions, initialSymbol, isOpen]);

  if (!isOpen) return null;

  const handleSetMaxAmount = () => {
    if (selectedBalance) setAmount(selectedBalance.balance);
  };

  const handleSubmit = async () => {
    setErrorMessage('');
    setStatusMessage('');

    if (!selectedBalance || !selectedOption) {
      setErrorMessage(formatMessageWithCode(t('send.noBalances'), t, 'NO_TOKEN_BALANCE'));
      return;
    }
    if (!ethers.isAddress(recipientAddress.trim())) {
      setErrorMessage(formatMessageWithCode(t('errors.invalidRecipientAddress'), t, 'INVALID_RECIPIENT_ADDRESS'));
      return;
    }
    if (!amount.trim()) {
      setErrorMessage(formatMessageWithCode(t('errors.sendAmountRequired'), t, 'SEND_AMOUNT_REQUIRED'));
      return;
    }

    setIsSending(true);
    const requestAuthUserId = authUserId;
    const requestWalletAddress = walletAddress;
    try {
      const submitted = isWorldAppSource
        ? await sendTokenWithWorldApp({
          tokenAddress: selectedBalance.address,
          recipientAddress: recipientAddress.trim(),
          amount,
          decimals: selectedBalance.decimals,
          rawBalance: selectedBalance.raw,
          walletAddress: walletAddress ?? '',
          transactionNotice: {
            title: t('send.transactionTitle'),
            description: t('send.transactionDescription', { amount, symbol: selectedOption.symbol }),
          },
        })
        : await sendTokenWithMetaMask({
          tokenAddress: selectedBalance.address,
          recipientAddress: recipientAddress.trim(),
          amount,
          decimals: selectedBalance.decimals,
          rawBalance: selectedBalance.raw,
          walletAddress: walletAddress ?? '',
        });
      setTxHash(submitted.txHash);
      setStatusMessage(t('swap.submitted'));
      setIsSending(false);
      setIsConfirming(true);

      void waitForOnChainTransaction(submitted.txHash)
        .then(async (result) => {
          setIsConfirming(false);
          if (
            result === 'confirmed'
            && walletAddress
            && requestAuthUserId
            && authUserIdRef.current === requestAuthUserId
            && walletAddressRef.current === requestWalletAddress
          ) {
            await recordWalletOperationBestEffort({
              operation_type: 'send',
              idempotency_key: buildWalletOperationIdempotencyKey('send', submitted.txHash),
              wallet_address: walletAddress,
              reference_type: 'wallet_send',
              metadata: {
                token_symbol: selectedOption.symbol,
                recipient_address: recipientAddress.trim().toLowerCase(),
              },
              legs: [
                ...submitted.approvalTxHashes.map((approvalTxHash, index) => ({
                  leg_type: 'approval' as const,
                  sequence_no: index,
                  chain_id: 480,
                  tx_hash: approvalTxHash,
                  token_address: selectedBalance.address,
                  token_symbol: selectedOption.symbol,
                })),
                {
                  leg_type: 'transfer',
                  sequence_no: submitted.approvalTxHashes.length,
                  chain_id: 480,
                  tx_hash: submitted.txHash,
                  user_op_hash: submitted.userOpHash,
                  from_address: walletAddress,
                  to_address: recipientAddress.trim(),
                  token_address: selectedBalance.address,
                  token_symbol: selectedOption.symbol,
                  amount_base_units: submitted.amountBaseUnits,
                },
              ],
            });
          }
          setStatusMessage(result === 'confirmed'
            ? t('send.confirmed')
            : t('swap.executionFailedStatus'));
          onSubmitted();
        })
        .catch((error: unknown) => {
          setIsConfirming(false);
          setStatusMessage(t('swap.submittedSlow'));
          setErrorMessage(formatErrorWithCode(error, t, 'errors.sendFailed'));
          onSubmitted();
        });
    } catch (error) {
      setIsSending(false);
      setErrorMessage(formatErrorWithCode(error, t, 'errors.sendFailed'));
    }
  };

  const canSubmit = Boolean(
      selectedBalance
      && recipientAddress.trim()
      && amount.trim()
      && canSend
      && !isSending
      && !isConfirming,
  );

  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !isSending && !isConfirming) onClose();
      }}
    >
      <section className="send-token-modal" role="dialog" aria-modal="true" aria-labelledby="send-token-title">
        <div className="modal-header">
          <div>
            <p className="eyebrow">WORLD CHAIN TRANSFER</p>
            <h2 id="send-token-title">{t('send.title')}</h2>
          </div>
          <button className="modal-close-button" type="button" onClick={onClose} disabled={isSending} aria-label={t('send.close')}>
            ×
          </button>
        </div>

        <div className="send-source-badge">
          <WalletSourceIcon source={balanceSource} />
          <div>
            <span>{t('send.currentAccount', { source: sourceLabel })}</span>
            <code>{walletAddress ? `${walletAddress.slice(0, 6)}…${walletAddress.slice(-4)}` : t('common.notConnected')}</code>
          </div>
        </div>

        {!canSend ? (
          <div className="send-environment-panel">
            <p>
              {t('send.browserOnlyMetaMask')}
            </p>
            {walletAppDeepLink ? (
              <a className="send-environment-link" href={walletAppDeepLink}>
                {t('send.sendInWorldApp')}
              </a>
            ) : (
              <p className="send-token-error" role="alert">{t('send.worldAppLinkMissing')}</p>
            )}
          </div>
        ) : balanceSource === 'metamask' && !walletAddress ? (
          <div className="send-environment-panel">
            <p>{t('send.browserMetaMaskOnly')}</p>
            {metaMaskError && <p className="send-token-error" role="alert">{metaMaskError}</p>}
            <button className="auth-button" type="button" onClick={() => void onConnectMetaMask()} disabled={isMetaMaskLoading}>
              {isMetaMaskLoading ? t('common.connect') : t('common.connectMetaMask')}
            </button>
          </div>
        ) : availableOptions.length === 0 ? (
          <p className="send-token-empty">{t('send.noBalances')}</p>
        ) : (
          <div className="send-token-form">
            {!isWorldAppSource && (
              <p className="send-token-status">{t('send.usingMetaMask')}</p>
            )}
            <label className="send-token-field">
              <span>{t('send.token')}</span>
              <select
                value={selectedOption?.symbol ?? ''}
                onChange={(event) => {
                  setSelectedSymbol(event.target.value as OnChainTokenSymbol);
                  setAmount('');
                }}
                disabled={isSending || isConfirming}
              >
                {availableOptions.map((option) => (
                  <option key={option.symbol} value={option.symbol}>{option.symbol} · {option.label}</option>
                ))}
              </select>
            </label>

            {selectedBalance && (
              <div className="send-token-available">
                <span>{t('send.availableBalance')}</span>
                <div className="send-token-available-value">
                  <strong>{formatOnChainTokenAmount(selectedBalance.raw, selectedBalance.decimals)} {selectedBalance.symbol}</strong>
                  <button type="button" onClick={handleSetMaxAmount} disabled={isSending || isConfirming}>{t('send.max')}</button>
                </div>
              </div>
            )}

            <label className="send-token-field">
              <span>{t('send.recipient')}</span>
              <input
                type="text"
                inputMode="text"
                autoComplete="off"
                placeholder="0x..."
                value={recipientAddress}
                onChange={(event) => setRecipientAddress(event.target.value)}
                disabled={isSending || isConfirming}
              />
            </label>

            <label className="send-token-field">
              <span>{t('send.amount')}</span>
              <input
                type="number"
                inputMode="decimal"
                min="0"
                step="any"
                placeholder="0.00"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                disabled={isSending || isConfirming}
              />
            </label>

            {errorMessage && <p className="send-token-error" role="alert">{errorMessage}</p>}
            {statusMessage && <p className="send-token-status" role="status">{statusMessage}</p>}
            {txHash && (
              <a
                className="send-token-hash"
                href={`https://worldscan.org/tx/${txHash}`}
                target={isWorldApp ? undefined : '_blank'}
                rel={isWorldApp ? undefined : 'noreferrer'}
              >
                {t('send.viewTransaction', { hash: `${txHash.slice(0, 10)}…${txHash.slice(-8)}` })}
              </a>
            )}

            <div className="send-token-actions">
              <button className="text-button" type="button" onClick={onClose} disabled={isSending}>
                {isConfirming ? t('common.backgroundConfirming') : t('common.cancel')}
              </button>
              <button className="auth-button" type="button" onClick={() => void handleSubmit()} disabled={!canSubmit}>
                {isSending
                  ? t('swap.waitingWallet', { source: isWorldAppSource ? 'World App' : 'MetaMask' })
                  : isConfirming ? t('common.confirming') : t('send.submit')}
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
